import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import {
  getOptimizelyExperimentDecisionVariation,
  getExperimentEnabledDecision,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'
import type { Experiment, ExperimentConfig } from 'constants/experiments'
import useExperimentActivation from './useExperimentActivation'

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

interface ExperimentResultBase<V extends string> {
  /** Whether the experiment is turned on for this user (i.e. they are bucketed into any variation, including control) */
  readonly isTurnedOn: boolean
  /** Whether the user is in a treatment variation (not control/off) */
  readonly isAnyVariant: boolean
  /** The raw variation key — undefined if the experiment is not turned on */
  readonly variation: V | undefined
  /**
   * Imperatively trigger the Snowplow activation event.
   *
   * Use this when activation should happen inside a callback (e.g. onClick,
   * onInView) rather than being tied to a boolean condition.
   *
   * Can be used alongside `canActivate` — activation fires when either
   * `canActivate` becomes `true` OR `triggerActivation()` is called.
   *
   * Calling this multiple times is safe — the underlying hook ensures the
   * Snowplow event only fires once per session.
   */
  readonly triggerActivation: () => void
}

/** Result when using a typed ExperimentConfig — `variation` is narrowed. */
type TypedExperimentResult<TVars extends ReadonlyArray<string>> =
  ExperimentResultBase<TVars[number]>

type UntypedExperimentResult = ExperimentResultBase<string>

interface UseExperimentOptions {
  /**
   * Declarative activation gate — when to fire the Snowplow activation event.
   * Defaults to `true` (immediate activation).
   *
   * Can be combined with `triggerActivation()` — activation fires when either
   * `canActivate` is `true` OR `triggerActivation()` has been called.
   */
  canActivate?: boolean
  /**
   * Bypass the standard UTM exclusion gate.
   *
   * Bypass the standard exclusion gate for experiments that intentionally
   * activate on normally excluded traffic.
   */
  overrideTrigger?: boolean
}

function hasExperimentDecision(variationKey: string | boolean | undefined): boolean {
  if (variationKey === undefined || variationKey === '') return false

  return String(variationKey).toLowerCase() !== 'off'
}

function getExperimentOverride(experimentProvider: ExperimentConfig['provider'], experimentId: Experiment['id']) {
  if (experimentProvider === 'cherto') {
    return config[`CHERTO_${experimentId}`]
  }

  return config[`OPTIMIZELY_${experimentId}`]
}

/**
 * Pass an experiment config from `Experiments` or `ChertoExperiments` to get a
 * narrowed `variation` field.
 *
 * @example
 * ```ts
 * import { Experiments } from 'constants/experiments'
 *
 * const { isAnyVariant, variation } = useExperiment(Experiments.croStructuredFinePrint)
 * // variation: 'control' | 'variant_1' | 'variant_2' | undefined
 *
 * if (variation === 'variant_3') {
 *   // ✅ TypeScript error: 'variant_3' doesn't exist for this experiment
 * }
 * ```
 */
function useExperiment<TExperiment extends Experiment>(
  config: TExperiment,
  options?: UseExperimentOptions,
): TypedExperimentResult<TExperiment['variations']>

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Single entry point for A/B experiments in React components.
 *
 * This hook does two things:
 * 1. **Reads the bucketed variation** — always available because bucketing happens during SSR.
 *    `isTurnedOn` and `isAnyVariant` are available immediately, even before activation fires.
 * 2. **Fires the Snowplow activation event** — via declarative `canActivate` option
 *    and/or imperative `triggerActivation()` callback.
 *
 * ### Activation modes
 *
 * **Declarative** — activation fires when a boolean condition becomes true:
 * ```ts
 * const { isAnyVariant } = useExperiment(Experiments.foo, { canActivate: inView })
 * ```
 *
 * **Imperative** — activation fires when you call `triggerActivation()`:
 * ```ts
 * const { isAnyVariant, triggerActivation } = useExperiment(Experiments.foo, { canActivate: false })
 * const onClick = () => { triggerActivation(); if (isAnyVariant) doThing() }
 * ```
 *
 * **Combined** — activation fires when either condition is met:
 * ```ts
 * const { triggerActivation } = useExperiment(Experiments.foo, { canActivate: inView })
 * // activates on scroll into view OR on manual trigger — whichever comes first
 * ```
 *
 * ### Why use this instead of the legacy Optimizely experiment hook?
 *
 * The old hook conflates "reading the variation" with "firing the activation event" via its
 * `canTrigger` parameter — returning `undefined` when `canTrigger` is `false`. This forces
 * developers to bypass the hook (reading Redux directly) when they need the variation for
 * layout decisions before the activation condition is met. That leads to dual-read patterns
 * that are error-prone and hard to understand.
 *
 * This hook always exposes `isTurnedOn` and `isAnyVariant` (derived from SSR bucketing), while
 * activation is controlled separately. One hook, one experiment ID, no way to get them out of sync.
 *
 * Pass an experiment config from `Experiments` or `ChertoExperiments` for type-safe variations:
 * ```ts
 * import { Experiments } from 'constants/experiments'
 * const { isAnyVariant } = useExperiment(Experiments.myExperiment)
 * ```
 *
 * @param experiment  An experiment config from `Experiments`/`ChertoExperiments`.
 * @param options.canActivate  When to fire the activation event. Defaults to `true`.
 */
function useExperiment(
  experimentConfig: Experiment,
  options?: UseExperimentOptions,
): UntypedExperimentResult {
  const experimentId = experimentConfig.id
  const experimentProvider = experimentConfig.provider ?? 'optimizely'
  const canActivate = options?.canActivate ?? true
  const overrideTrigger = options?.overrideTrigger ?? false

  // Always read the SSR-bucketed variation from Redux (no activation side-effect).
  const variation = useAppSelector((state): string | undefined => {
    if (!overrideTrigger && !isAllowedToTriggerExperiment(true, state.utm.content, experimentId)) {
      return undefined
    }

    const overriddenVariation = getExperimentOverride(experimentProvider, experimentId)
    if (overriddenVariation !== undefined) {
      return overriddenVariation
    }

    if (experimentProvider === 'cherto') {
      const chertoExperiment = state.cherto.chertoExperiments[experimentId]
      const variationKey = chertoExperiment?.variationKey
      if (!variationKey || variationKey.toLowerCase() === 'off') {
        return undefined
      }
      return variationKey
    }
    if (experimentProvider === 'optimizely') {
      const optimizelyExperiment = state.optimizely.optimizelyExperiments[experimentId]
      const variationKey = optimizelyExperiment?.variationKey
      if (!variationKey || variationKey.toLowerCase() === 'off') {
        return undefined
      }
      return optimizelyExperiment?.variables[variationKey] ?? variationKey
    }
  })

  const isTurnedOn = useAppSelector((state): boolean => {
    if (!overrideTrigger && !isAllowedToTriggerExperiment(true, state.utm.content, experimentId)) {
      return false
    }

    const overriddenVariation = getExperimentOverride(experimentProvider, experimentId)
    if (overriddenVariation !== undefined) {
      return hasExperimentDecision(overriddenVariation)
    }

    const experiment = experimentProvider === 'cherto' ?
      state.cherto.chertoExperiments[experimentId] :
      state.optimizely.optimizelyExperiments[experimentId]

    return hasExperimentDecision(experimentProvider === 'cherto' ?
      experiment?.variationKey :
        getOptimizelyExperimentDecisionVariation(state, experimentId))
  })

  // Fire the activation event when either:
  // - canActivate is true (declarative), OR
  // - triggerActivation() is called (imperative)
  const activate = useExperimentActivation(experimentId, canActivate, overrideTrigger, experimentProvider)
  const triggerActivation = useCallback(() => {
    activate()
  }, [activate])

  return useMemo<UntypedExperimentResult>(() => {
    if (!isTurnedOn) {
      return {
        isTurnedOn: false,
        isAnyVariant: false,
        variation: undefined,
        triggerActivation,
      }
    }

    return {
      isTurnedOn: true,
      isAnyVariant: getExperimentEnabledDecision(variation),
      variation,
      triggerActivation,
    }
  }, [isTurnedOn, variation, triggerActivation])
}

export default useExperiment
